import React from 'react';
import {useIntl} from 'react-intl';
import config from '../../../components/canvas/store/sqftConfig';

export const MiteredEdgeDiagram = () => (
  <svg
    className="sqft-edge-help__diagram-svg"
    viewBox="0 0 300 170"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
  >
    <title>Mitered edge cross-section: a strip of material is added under the front edge so the finished edge height equals slab thickness plus the added strip, along the full edge length.</title>

    {/* countertop slab, side view */}
    <rect x="20" y="50" width="200" height="16" fill="#d9d9d9" stroke="currentColor" strokeWidth="1.5" />
    {/* extra material added under the front edge, width matches slab thickness so the miter meets the corner */}
    <rect x="204" y="66" width="16" height="22" fill="#f5a623" stroke="currentColor" strokeWidth="1.5" />
    {/* miter seam, cut at a true 45 degrees straight into the front corner */}
    <line x1="204" y1="66" x2="220" y2="50" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" />

    {/* 45 degree angle indicator at the miter seam */}
    <path d="M 220 60 A 10 10 0 0 1 212.9 57.1" fill="none" stroke="currentColor" strokeWidth="1" />
    <text x="220" y="42" fontSize="10" fontWeight="normal" textAnchor="middle" fill="currentColor">45°</text>

    {/* Edge Length dimension */}
    <line x1="20" y1="110" x2="220" y2="110" stroke="currentColor" strokeWidth="1" markerStart="url(#mEdgeArrowStart)" markerEnd="url(#mEdgeArrowEnd)" />
    <text x="120" y="126" fontSize="11" fontWeight="normal" textAnchor="middle" fill="currentColor">Edge Length</text>

    {/* Edge Height dimension */}
    <line x1="250" y1="50" x2="250" y2="88" stroke="currentColor" strokeWidth="1" markerStart="url(#mEdgeArrowStart)" markerEnd="url(#mEdgeArrowEnd)" />
    <text x="272" y="69" fontSize="11" fontWeight="normal" textAnchor="middle" fill="currentColor" transform="rotate(90 272 69)">Edge Height</text>

    <defs>
      <marker id="mEdgeArrowEnd" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
      </marker>
      <marker id="mEdgeArrowStart" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
        <path d="M6,0 L0,3 L6,6 Z" fill="currentColor" />
      </marker>
    </defs>
  </svg>
);

export const WaterfallEdgeDiagram = () => (
  <svg
    className="sqft-edge-help__diagram-svg"
    viewBox="0 0 300 181"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
  >
    <title>Waterfall edge cross-section: the countertop material continues straight down the side of the cabinet to the floor along the full edge length.</title>

    {/* cabinet body, for context */}
    <rect x="30" y="61" width="120" height="78" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />

    {/* countertop slab, side view */}
    <rect x="20" y="45" width="140" height="16" fill="#d9d9d9" stroke="currentColor" strokeWidth="1.5" />
    {/* waterfall panel, extra material dropping to the floor */}
    <rect x="160" y="45" width="16" height="94" fill="#f5a623" stroke="currentColor" strokeWidth="1.5" />
    {/* miter seam, cut at a true 45 degrees from the outer corner to the inner corner */}
    <line x1="176" y1="45" x2="160" y2="61" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" />

    {/* 45 degree angle indicator at the corner seam */}
    <path d="M 176 55 A 10 10 0 0 1 168.9 52.1" fill="none" stroke="currentColor" strokeWidth="1" />
    <text x="176" y="37" fontSize="10" fontWeight="normal" textAnchor="middle" fill="currentColor">45°</text>

    {/* floor line */}
    <line x1="10" y1="139" x2="270" y2="139" stroke="currentColor" strokeWidth="1.5" />

    {/* Edge Length dimension */}
    <line x1="20" y1="28" x2="160" y2="28" stroke="currentColor" strokeWidth="1" markerStart="url(#wEdgeArrowStart)" markerEnd="url(#wEdgeArrowEnd)" />
    <text x="90" y="20" fontSize="11" fontWeight="normal" textAnchor="middle" fill="currentColor">Edge Length</text>

    {/* Waterfall Height dimension */}
    <line x1="210" y1="45" x2="210" y2="139" stroke="currentColor" strokeWidth="1" markerStart="url(#wEdgeArrowStart)" markerEnd="url(#wEdgeArrowEnd)" />
    <text x="230" y="92" fontSize="11" fontWeight="normal" textAnchor="middle" fill="currentColor" transform="rotate(90 230 92)">Waterfall Height</text>

    <defs>
      <marker id="wEdgeArrowEnd" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
      </marker>
      <marker id="wEdgeArrowStart" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
        <path d="M6,0 L0,3 L6,6 Z" fill="currentColor" />
      </marker>
    </defs>
  </svg>
);

const getMiteredDefaultHeightLabel = (intl) => {
  const value = config.measureUnit === "Imperial"
    ? config.miteredEdgeAdditionalImperialArea
    : config.miteredEdgeAdditionalMetricArea;

  if (!value) {
    return intl.formatMessage({id: "sqft.navigation.edges.formula.none"});
  }

  return config.measureUnit === "Imperial" ? `${value} in` : `${value} mm`;
};

const getWaterfallDefaultHeightLabel = () => {
  return config.measureUnit === "Imperial"
    ? `${config.waterfallImperialHeight} in`
    : `${config.waterfallMetricHeight} mm`;
};

export const MiteredEdgeHelpContent = () => {
  const intl = useIntl();
  const defaultHeight = getMiteredDefaultHeightLabel(intl);

  return (
    <div className="sqft-edge-help">
      <p className="sqft-edge-help__text">
        {intl.formatMessage({id: "sqft.navigation.edges.miteredHelp.text"})}
      </p>
      <div className="sqft-edge-help__formula">
        <p className="sqft-edge-help__formula-main">
          {intl.formatMessage({id: "sqft.navigation.edges.miteredHelp.formula"})}
        </p>
        <p className="sqft-edge-help__formula-note">
          {intl.formatMessage({id: "sqft.navigation.edges.miteredHelp.formulaNote"}, {defaultHeight})}
        </p>
      </div>
      <div className="sqft-edge-help__diagram">
        <MiteredEdgeDiagram />
      </div>
    </div>
  );
};

export const WaterfallEdgeHelpContent = () => {
  const intl = useIntl();
  const defaultHeight = getWaterfallDefaultHeightLabel();

  return (
    <div className="sqft-edge-help">
      <p className="sqft-edge-help__text">
        {intl.formatMessage({id: "sqft.navigation.edges.waterfallHelp.text"})}
      </p>
      <div className="sqft-edge-help__formula">
        <p className="sqft-edge-help__formula-main">
          {intl.formatMessage({id: "sqft.navigation.edges.waterfallHelp.formula"})}
        </p>
        <p className="sqft-edge-help__formula-note">
          {intl.formatMessage({id: "sqft.navigation.edges.waterfallHelp.formulaNote"}, {defaultHeight})}
        </p>
      </div>
      <div className="sqft-edge-help__diagram">
        <WaterfallEdgeDiagram />
      </div>
    </div>
  );
};
